<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	// Stores
	import MeetingStore from '@/Stores/Racing/MeetingStore.js';
	import RaceStore from '@/Stores/Racing/RaceStore.js';
	import PreferenceStore from '@/Stores/PreferenceStore.js';
	import ClassificationStore from '@/Stores/Racing/ClassificationStore';
	import MultiLegEventStore from '@/Stores/Racing/MultiLegEventStore';
	import ACLStore from '@/Stores/ACLStore.js';
	// Partials
	import MeetingSelector from '@/Partials/Racing/Meeting/_MeetingSelector.svelte';
	import ManageVisibility from '@/Partials/Racing/Meeting/_ManageVisibility.svelte';
	import QuaddieManualResult from '@/Partials/Racing/Meeting/_QuadrellaManualResult.svelte';
	import EarlyQuaddieManualResult from '@/Partials/Racing/Meeting/_EarlyQuaddieManualResult.svelte';
	import UpdateStatus from '@/Partials/Racing/Race/_UpdateStatus.svelte';
	import ExoticBets from '@/Partials/Racing/Race/_ExoticBets.svelte';
	import SrmBets from '@/Partials/Racing/Race/_SrmBets.svelte';
	import CashoutBets from '@/Partials/Racing/Race/_CashoutBets.svelte';
	import UnresultedBets from '@/Partials/Racing/Race/_UnresultedBets.svelte';
	import AbandonMeeting from '@/Partials/Racing/Meeting/_AbandonMeeting.svelte';
	import AdjustSelectionLiability from '@/Partials/Liabilities/_AdjustSelectionLiability.svelte';
	import AdjustMeetingFees from '@/Partials/Racing/Meeting/_AdjustMeetingFees.svelte';
	import ReorderMeetings from '@/Partials/Racing/Meeting/_ReorderMeetings.svelte';
	// Components
	import TimeToStart from '@/Components/TimeToStart.svelte';
	import Label from '@/Components/Label.svelte';
	import FontIcon from '@/Components/FontIcon.svelte';
	import Switch from '@/Components/Switch.svelte';
	import Datepicker from '@/Components/Datepicker.svelte';
	import Icon from '@/Components/Icon.svelte';
	// Constants
	import { EventStatus } from '@/Constants/Event.js';
	import config from '@/config.js';
	import { BetType } from '@/Constants/Bet';
	// Services
	import { notify } from '@/Services/Notify';
	import { CurrencyFormat, NumberTransform } from '@/Services/Helpers';

	dayjs.extend(localizedFormat);

	export let selectedMeetingId;
	export let selectedRaceId;
	export let daySelected;

	ClassificationStore.getClassifications();

	$: meeting = selectedMeetingId && $MeetingStore && $MeetingStore.firstWhere('id', selectedMeetingId);
	$: race = meeting && selectedRaceId && $RaceStore && $RaceStore.firstWhere('id', selectedRaceId);
	$: holdAmount = (race || includeMulti || !includeMulti) && calcHoldAmount();
	$: (includeMulti || !includeMulti) && PreferenceStore.updateSetting('racing.racingIncludeMulti', includeMulti);
	$: classification =
		race &&
		race.classification &&
		$ClassificationStore &&
		$ClassificationStore.firstWhere('id', race.classification.id.toString());
	$: currentLiabilityLimit = meeting && race && classification && getCurrentLiabilityLimit();
	$: currentMultiLiabilityLimit = meeting && race && classification && getCurrentMultiLiabilityLimit();

	let manageVisibilityOpen = false;
	let showExoticBets = false;
	let showSrmBets = false;
	let showUnresultedBets = false;
	let showCashoutBets = false;
	let showAbandonMeeting = false;
	let showLiabilityAdjustment = false;
	let showMeetingFeesAdjustment = false;
	let includeMulti = $PreferenceStore.racing.racingIncludeMulti ? true : false;
	let selected = dayjs(daySelected).toDate();
	let showQuaddieManualResult = false;
	let showEarlyQuaddieManualResult = false;
	let showReorderMeetings = false;

	function getCurrentLiabilityLimit() {
		if (race.liabilities && race.liabilities.settings && race.liabilities.settings.win_selection_limit) {
			return CurrencyFormat(race.liabilities.settings.win_selection_limit);
		}
		if (classification && classification.selection_liability_limit !== null) {
			return CurrencyFormat(classification.selection_liability_limit);
		}

		return 'None set';
	}

	function getCurrentMultiLiabilityLimit() {
		if (race.liabilities && race.liabilities.settings && race.liabilities.settings.win_multi_selections_limit) {
			return CurrencyFormat(race.liabilities.settings.win_multi_selections_limit);
		}
		if (classification && classification.multi_selections_liability_limit !== null) {
			return CurrencyFormat(classification.multi_selections_liability_limit);
		}

		return 'None set';
	}

	function dateChanged(dateChosen) {
		selectedMeetingId = undefined;
		selectedRaceId = undefined;
		daySelected = dayjs(dateChosen).format('YYYY-MM-DD');
	}

	function calcHoldAmount() {
		let hold = (race && race.liabilities && race.liabilities.hold) || 0;
		hold = parseInt(hold) + parseInt(calcBetTypeHold('cashout_hold') * 100);
		if (includeMulti) {
			hold = parseInt(hold) + parseInt(calcBetTypeHold('multi_hold') * 100);
			hold = parseInt(hold) + parseInt(calcBetTypeHold('cashout_multi_hold') * 100);
		}
		return CurrencyFormat(hold);
	}

	function calcBetTypeHold(liabilityType) {
		let hold = (race && race.liabilities && race.liabilities[liabilityType]) || 0;
		return NumberTransform(hold);
	}

	async function openQuadrellaManualResult() {
		await MultiLegEventStore.getForMeeting(selectedMeetingId, BetType.Quadrella)
			.then(() => {
				if ($MultiLegEventStore.count() > 0) {
					showQuaddieManualResult = true;
					return;
				}
				notify({
					type: 'info',
					title: 'No quaddie found!',
					message: "Sorry this meeting doesn't have a quaddie",
					timeout: 3
				});
			})
			.catch((e) => {
				console.error(e);
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Error getting quaddie details for meeting',
					timeout: 3
				});
			});
	}

	async function openEarlyQuadrellaManualResult() {
		await MultiLegEventStore.getForMeeting(selectedMeetingId, BetType.EarlyQuadrella)
			.then(() => {
				if ($MultiLegEventStore.count() > 0) {
					showEarlyQuaddieManualResult = true;
					return;
				}
				notify({
					type: 'info',
					title: 'No early quaddie found!',
					message: "Sorry this meeting doesn't have an early quaddie",
					timeout: 3
				});
			})
			.catch((e) => {
				console.error(e);
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Error getting early quaddie details for meeting',
					timeout: 3
				});
			});
	}
</script>

{#if manageVisibilityOpen}
	<ManageVisibility bind:open={manageVisibilityOpen} {meeting} />
{/if}
{#if showExoticBets}
	<ExoticBets bind:open={showExoticBets} {meeting} {race} />
{/if}
{#if showSrmBets}
	<SrmBets bind:open={showSrmBets} {meeting} {race} />
{/if}
{#if showCashoutBets}
	<CashoutBets bind:open={showCashoutBets} {meeting} {race} />
{/if}
{#if showAbandonMeeting}
	<AbandonMeeting bind:open={showAbandonMeeting} {meeting} />
{/if}
{#if showLiabilityAdjustment}
	<AdjustSelectionLiability bind:open={showLiabilityAdjustment} {meeting} {race} />
{/if}
{#if showMeetingFeesAdjustment}
	<AdjustMeetingFees bind:open={showMeetingFeesAdjustment} {meeting} />
{/if}
{#if showQuaddieManualResult}
	<QuaddieManualResult bind:open={showQuaddieManualResult} {meeting} />
{/if}
{#if showEarlyQuaddieManualResult}
	<EarlyQuaddieManualResult bind:open={showEarlyQuaddieManualResult} {meeting} />
{/if}
{#if showUnresultedBets}
	<UnresultedBets bind:open={showUnresultedBets} {meeting} {race} />
{/if}
{#if showReorderMeetings}
	<ReorderMeetings bind:open={showReorderMeetings} selectedDate={daySelected} />
{/if}
{#if meeting}
	<div class="container">
		<div class="flex justify-between">
			<div class="flex items-center">
				<MeetingSelector {selectedMeetingId} {race} />
				<div class="flex ml-1" style="height:32px; width:32px;">
					<Datepicker
						bind:selected
						dateChosen={(date) => {
							dateChanged(date);
						}}
						height="32px"
						btnStyle="flex items-center justify-center p-2 text-white rounded bg-dark-grey hover:bg-grey focus:outline-none flex justify-center max-w-8 min-w-8 w-8"
					>
						<Icon name="calendar" />
					</Datepicker>
				</div>
				<button
					on:click={() => (manageVisibilityOpen = true)}
					title="Show/Hide Meeting or Races"
					class="ml-0.5 flex justify-center items-center text-white rounded bg-dark-grey hover:bg-grey focus:outline-none"
					style="height:32px; min-height: 32px; max-height: 32px;width:32px; min-width: 32px; max-width: 32px;"
				>
					<Icon name="hide" />
				</button>
				{#if race}
					<button
						on:click={() => (showAbandonMeeting = true)}
						title="Abandon Meeting or Races"
						class="ml-0.5 flex items-center justify-center text-white rounded bg-dark-grey hover:bg-grey focus:outline-none"
						style="height:32px; min-height: 32px; max-height: 32px;width:32px; min-width: 32px; max-width: 32px;"
					>
						<Icon name="cross" classes="w-5 h-5" />
					</button>
					<button
						on:click={() => openQuadrellaManualResult()}
						title="Manually Result Quaddie"
						class="px-2 pt-1.5 pb-2.5 ml-0.5 text-white rounded bg-dark-grey hover:bg-grey focus:outline-none flex items-center justify-center text-xl"
						style="height:32px; min-height: 32px; max-height: 32px;width:32px; min-width: 32px; max-width: 32px;"
					>
						<span class="icon-q font-extrabold mb-1">Q</span>
					</button>
					<button
						on:click={() => openEarlyQuadrellaManualResult()}
						title="Manually Result Early Quaddie"
						class="px-2 pt-1.5 pb-2.5 ml-0.5 text-white rounded bg-dark-grey hover:bg-grey focus:outline-none flex items-center justify-center text-xl"
						style="height:32px; min-height: 32px; max-height: 32px;width:32px; min-width: 32px; max-width: 32px;"
					>
						<span class="icon-q font-extrabold mb-1">EQ</span>
					</button>
					<button
						on:click={() => (showReorderMeetings = true)}
						title="Reorder meetings"
						class="ml-0.5 text-white rounded bg-dark-grey hover:bg-grey focus:outline-none"
						style="height:32px; min-height: 32px; max-height: 32px;width:32px; min-width: 32px; max-width: 32px;"
					>
						<Icon name="sort" classes="block w-5 h-5 inline" />
					</button>
				{/if}
				<div class="ml-1">{dayjs(daySelected).format('ll')}</div>
			</div>
			<div class="flex flex-col items-end">
				{#if race}
					<div class="font-semibold">
						R{race && race.race_number} - {race && dayjs(race.start_time).format('LT')}
					</div>
					<div class="flex items-center mt-1 text-sm">
						{#if race.status.id === EventStatus.Open || race.status.id === EventStatus.ReOpened}
							<TimeToStart start_time={race.start_time} />
						{/if}
						<UpdateStatus {race} meetingId={selectedMeetingId} />
					</div>
				{/if}
			</div>
		</div>
		<div class="mt-2 font-semibold">
			{#if race}
				<a href="{config.RACE_URL}{race.id}" target="_blank" rel="noreferrer">
					<FontIcon icon={race.race_type.name.toLowerCase()} />
					{meeting.name} Race {race && race.race_number} - {race.name}
				</a>
			{:else}
				<FontIcon icon={meeting.race_type.name.toLowerCase()} />
				{meeting.name}
			{/if}
		</div>
		<div class="flex justify-between mt-2">
			{#if race}
				<div class="space-y-1 w-4/5">
					<!-- ...LEFT... -->

					<div class="grid grid-cols-7 text-center">
						<div class="col-start-1 text-sm font-semibold">Event Hold</div>
						<div class="text-sm font-semibold">Win</div>
						<div class="text-sm font-semibold">Plc</div>
						<div class="text-sm font-semibold">Multi</div>
						<div class="text-sm font-semibold">
							<button
								title="Show Exotic Bets"
								class="px-1 font-semibold bg-orange-300 rounded hover:bg-orange-400 focus:outline-none"
								on:click={() => (showExoticBets = true)}
							>
								Exotic
							</button>
						</div>
						<div class="text-sm font-semibold">
							<button
								title="Show SRM Bets"
								class="px-1 font-semibold bg-orange-300 rounded hover:bg-orange-400 focus:outline-none"
								on:click={() => (showSrmBets = true)}
							>
								SRM
							</button>
						</div>
						<div class="text-sm font-semibold">
							<button
								title="Show SRM Bets"
								class="px-1 font-semibold bg-orange-300 rounded hover:bg-orange-400 focus:outline-none"
								on:click={() => (showCashoutBets = true)}
							>
								Cashout
							</button>
						</div>
						<div class="col-start-1 px-1">{holdAmount}</div>
						<div class="px-1">{race && CurrencyFormat(calcBetTypeHold('win_hold'), true)}</div>
						<div class="px-1">{race && CurrencyFormat(calcBetTypeHold('place_hold'), true)}</div>
						<div class="px-1">{race && CurrencyFormat(calcBetTypeHold('multi_hold'), true)}</div>
						<div class="px-1">{race && CurrencyFormat(calcBetTypeHold('exotics_hold'), true)}</div>
						<div class="px-1">{race && CurrencyFormat(calcBetTypeHold('srm_hold'), true)}</div>
						<div class="px-1">
							{race &&
								CurrencyFormat(
									includeMulti
										? parseInt(calcBetTypeHold('cashout_hold').toString()) +
												parseInt(calcBetTypeHold('cashout_multi_hold').toString())
										: parseInt(calcBetTypeHold('cashout_hold').toString()),
									true
								)}
						</div>
					</div>
					<div class="flex flex-row justify-between items-center">
						<div>
							Include Multi
							<Switch name="incMulti" bind:checked={includeMulti} small={true} />
						</div>
						<button
							class="px-2 py-1 border hover:bg-orange-400 border-orange-300 hover:text-white focus:outline-none"
							on:click={() => (showUnresultedBets = true)}
						>
							Unresulted
						</button>
					</div>

					<div class="pt-2 flex flex-row flex-grow justify-between items-center">
						<div
							class="hover:border-blue-700 border-white border-2 hover:bg-blue-300 cursor-pointer justify-between"
							on:click={() => {
								showLiabilityAdjustment = true;
							}}
						>
							<span>Liability Limit: {currentLiabilityLimit}</span>
							<span class="pl-6">Multi Liability Limit: {currentMultiLiabilityLimit}</span>
						</div>
						<div
							class="border-white border-2"
							class:hover:border-orange-700={$ACLStore && ACLStore.granted('competitions.update.fees')}
							class:hover:bg-orange-300={$ACLStore && ACLStore.granted('competitions.update.fees')}
							class:cursor-pointer={$ACLStore && ACLStore.granted('competitions.update.fees')}
							on:click={() => {
								if ($ACLStore && ACLStore.granted('competitions.update.fees')) {
									showMeetingFeesAdjustment = true;
								}
							}}
						>
							Fees: {meeting.fees ? meeting.fees + '%' : '-'}
						</div>
					</div>
				</div>
			{/if}
			<div class="flex flex-col justify-end">
				<!-- ...RIGHT... -->
				{#if !meeting.visible}
					<Label label="Meeting Hidden" classes="mb-1" />
				{/if}
				{#if race}
					<div class="pb-px mb-px">Pays: {race.number_of_places} places</div>
				{/if}
			</div>
		</div>
	</div>
{/if}
